// Eine Station der Besichtigung (Mockup /doku/station/:nr). Jede ist gleich
// aufgebaut: die Erklärung, die Fotos (seit Schritt 2: Kachel „Foto“ öffnet
// die Kamera, ein Foto antippen öffnet es groß), die Notiz zur ganzen
// Station – getippt, mit dem Mikrofon der Tastatur diktiert oder (seit
// Schritt 3) als Sprachmemo eingesprochen –, die Längen für Kabelkanal, Rohr
// und Kabel, „Sonstiges“ und der Platz für die Fachfragen (Stufe 2). Unten
// fest: links die vorige Station mit ihrem Namen („‹ Dach“; in Station 1
// „‹ Übersicht“) und „Station fertig“; nach der letzten Station geht es zur
// Dachplanung mit K2. Oben links immer „‹ Übersicht“ – seit 19.10.2026
// beschriftet, weil ein Pfeil oben und ein „Zurück“ unten mit
// verschiedenen Zielen beim Augentest am iPhone verwirrten.
//
// Alles wird beim Tippen am Gerät gespeichert (kurz entprellt) und für den
// Server vorgemerkt – auch ohne Empfang.
//
// Seit Schritt 4: Ist die Besichtigung gesendet, ist die Station nur zum
// Lesen da – Fotos ohne Kachel „Foto“, Notiz und „Sonstiges“ als Text,
// Memos zum Abspielen, Längen als Werte; unten „Weiter“ statt „Station
// fertig“. Eine Seite mit Modus, keine zweite Seite.
//
// Seit Schritt 5 der dritte Modus, „Etwas nachreichen“: Die Kachel „Foto“
// ist wieder da (neue Fotos tragen die Marke „neu“, alte öffnen sich nur
// zum Ansehen); die gesendete Notiz steht als Text da, darunter der
// „Nachtrag zur Notiz“ zum Tippen, Diktieren und für Sprachmemos; Längen und „Sonstiges“
// lassen sich berichtigen (Marke „berichtigt“ oder „neu“). „Station
// fertig“ gibt es nicht – unten „Weiter“. Beim Nachsehen steht, was
// nachgereicht wurde, mit Marke da, die Notiz eines Nachtrags unter
// „Nachgereicht am …“.
//
// Seit E3 (S6), wenn die Besichtigung gesendet ist: oben die Fragen an der
// Station, unter einer Notiz die Fragen an ihr, an den Fotos eine Marke, wo
// eine Frage offen ist; unten „Frage stellen“ zu dieser Station.
//
// Seit 22.10.2026 (O59): Unter Notiz und „Sonstiges“ statt der langen
// Hinweise nur „Wie diktiere ich?“ (DiktatHilfe) – der Platzhalter sagt
// „Tippen oder diktieren“, der Rest klappt dort auf.
//
// Seit E3b (O65, Gernot 22.10.2026): Unter der Notiz der Knopf „Material“ –
// er öffnet die Seite „Material“ für diese Station (MaterialSeite.tsx), was
// von hier kam, steht darüber. Kabel, Rohr und Kabelkanal sind Material: Das
// Feld „Längen“ zeigt eine Station nur noch, wenn die Besichtigung schon
// Längen hat (vor dem Update begonnen) – dort geht es weiter wie gewohnt.
// Nach der letzten Station geht es zu „Material“, dann zur Dachplanung.
//
// Seit E3c (S5; ANFORDERUNGEN.md H32): An der Station Dach vor „Sonstiges“
// „Für die Dachpartie“ – Höhe bis zur Dachrinne, Zugang, ein Satz
// (DachFelder.tsx); freiwillig, im Nachtrag berichtigbar wie „Sonstiges“.

import { useRef, useState } from "react";
import { LAENGEN_ARTEN, STATIONEN, type IchAntwort } from "@happy-energy/kern";
import { AppFuss, AppKopf, Chip, DiktatHilfe, Ikon, Spaeter, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { laengeSpeichern, meterLesen, meterZeigen, notizSpeichern, sonstigesSpeichern, stationFertig, type Bearbeiten } from "../arbeitskopie.ts";
import { materialAn, materialMarke, materialOrdnen, materialText, mitLaengen } from "../material.ts";
import type { Laenge, Materialangabe, Notiz, Station } from "../speicher.ts";
import { STATIONEN_ZAHL } from "../postausgang.ts";
import { fragenAmGeraet, gespraechMoeglich, type FrageAmGeraet } from "../gespraech.ts";
import { aufnahmeLesen, memosZurNotiz, modusVon, nachgereichtAm, type AufnahmeGanz } from "./Uebersicht.tsx";
import { Gespeichert, LeseText, SendeStand, speichernFehlgeschlagen, useLive, usePostausgang, useSpeichern, ZUM_START } from "./gemeinsam.tsx";
import { FotoRaster, sendungsMarke } from "./fotos.tsx";
import { MemoListe, NotizFuss } from "./memos.tsx";
import { FragenAbschnitt, FrageStellen } from "./fragen.tsx";
import { DachFelder } from "./DachFelder.tsx";

const TEXT_GRENZE = 5000;

type Feld = "notiz" | "sonstiges" | `laenge-${string}`;

/** Warum eine Länge (noch) nicht gespeichert ist: keine brauchbare Zahl – oder im Nachtrag geleert, wo das nicht geht. */
type LaengeProblem = "ungueltig" | "leer";

/**
 * Die Marke einer Länge: im Nachtrag „neu“ oder „berichtigt“, beim
 * Nachsehen „nachgereicht“ oder „berichtigt“ (seit Schritt 5).
 */
function laengeMarke(l: Laenge | undefined, modus: Bearbeiten): string | null {
  if (!l || l.meter === null) return null;
  if (modus.art === "nachtrag") return l.nachtragId === modus.nachtragId ? "neu" : l.geaendertImNachtrag === modus.nachtragId ? "berichtigt" : null;
  if (modus.art === "gesperrt") return l.geaendertImNachtrag ? "berichtigt" : l.nachtragId ? "nachgereicht" : null;
  return null;
}

function LaengeFeld({
  station,
  art,
  anfang,
  marke,
  leerGehtNicht,
  gespeichert,
  problem,
}: {
  station: Station;
  art: { schluessel: string; name: string; einheit: string };
  anfang: number | null;
  /** „neu“, „berichtigt“ – im Nachtrag. */
  marke: string | null;
  /** Im Nachtrag eine gesendete Länge: berichtigen ja, leeren nein (trag 0 ein). */
  leerGehtNicht: boolean;
  gespeichert: (feld: Feld) => void;
  /** Meldet, ob die Eingabe eine brauchbare Länge ist – der Hinweis steht unter allen dreien. */
  problem: (art: string, p: LaengeProblem | null) => void;
}) {
  const [text, setText] = useState(() => meterZeigen(anfang));
  const gelesen = meterLesen(text);
  const falsch = gelesen === undefined || (gelesen === null && leerGehtNicht);
  const speichern = useSpeichern(async (meter: number | null) => {
    await laengeSpeichern(station, art.schluessel, meter);
    gespeichert(`laenge-${art.schluessel}`);
  });
  const id = `laenge-${art.schluessel}`;
  return (
    <label className={`laenge-feld${falsch ? " ungueltig" : ""}`} htmlFor={id}>
      <span className="laenge-name">
        {art.name}
        {marke && <span className="sendungs-marke">{marke}</span>}
      </span>
      <span className="laenge-wert">
        <input
          id={id}
          inputMode="decimal"
          enterKeyHint="done"
          autoComplete="off"
          placeholder="–"
          maxLength={9}
          value={text}
          aria-invalid={falsch ? true : undefined}
          onChange={(e) => {
            setText(e.target.value);
            const meter = meterLesen(e.target.value);
            if (meter === null && leerGehtNicht) {
              // Nichts speichern – auch nicht, was eben noch getippt war: Es gilt der gesendete (oder zuletzt berichtigte) Wert.
              speichern.verwerfen();
              problem(art.schluessel, "leer");
              return;
            }
            problem(art.schluessel, meter === undefined ? "ungueltig" : null);
            if (meter !== undefined) speichern.spaeter(meter);
          }}
          onBlur={speichern.sofort}
        />
        <span className="einheit">{art.einheit}</span>
      </span>
    </label>
  );
}

/** Eine Länge nur zum Lesen – dieselbe Kachel, die Zahl statt des Feldes. */
function LaengeWert({ art, meter, marke }: { art: { name: string; einheit: string }; meter: number | null; marke: string | null }) {
  return (
    <div className="laenge-feld nur-lesen">
      <span className="laenge-name">
        {art.name}
        {marke && <span className="sendungs-marke">{marke}</span>}
      </span>
      <span className="laenge-wert">
        <span className={`laenge-zahl${meter === null ? " leer" : ""}`}>{meter === null ? "–" : meterZeigen(meter)}</span>
        <span className="einheit">{art.einheit}</span>
      </span>
    </div>
  );
}

/**
 * Das Material aus dieser Station (seit E3b): kurz, was eingetragen ist, und
 * der dezente Knopf „Material“ – beides führt zur Seite „Material“ der
 * Station, dort lässt es sich ändern. Gesendet ohne Material: nichts.
 */
function MaterialKurz({ material, adresse, modus }: { material: readonly Materialangabe[]; adresse: string; modus: Bearbeiten }) {
  const nurLesen = modus.art === "gesperrt";
  if (nurLesen && material.length === 0) return null;
  return (
    <>
      {material.length > 0 && (
        <>
          <div className="abschnitt-titel">Material</div>
          <a href={adresse} className="material-kurz">
            <span className="material-kurz-liste">
              {material.map((m) => {
                const marke = materialMarke(m, modus);
                return (
                  <span key={m.id}>
                    {materialText(m)}
                    {marke && <span className="sendungs-marke">{marke}</span>}
                  </span>
                );
              })}
            </span>
            <Ikon name="rechts" />
          </a>
        </>
      )}
      {!nurLesen && (
        <a href={adresse} className="material-knopf">
          <span className="material-knopf-zeichen" aria-hidden="true">
            <Ikon name="paket" groesse="k" />
          </span>
          {material.length > 0 ? "Material ändern oder dazu" : "Material"}
        </a>
      )}
    </>
  );
}

/**
 * Eine Notiz nur zum Lesen: der Text und ihre Memos – nur eingesprochen, dann nur die Memos. `kopf`: darüber, etwa „Nachgereicht am …“.
 * Darunter die Fragen an dieser Notiz (seit E3).
 */
function NotizLesen({
  ganz,
  notiz,
  leer,
  kopf,
  fragen,
  ich,
}: {
  ganz: AufnahmeGanz;
  notiz: Notiz | undefined;
  leer: string;
  kopf?: string;
  fragen: readonly FrageAmGeraet[];
  ich: { id: string; name: string };
}) {
  const memos = memosZurNotiz(ganz, notiz);
  const hier = notiz ? fragen.filter((f) => f.bezug.tabelle === "notiz" && f.bezug.id === notiz.id) : [];
  return (
    <>
      <div className="text-karte nur-lesen">
        {kopf && <span className="sendungs-kopf">{kopf}</span>}
        {(notiz?.text.trim() || memos.length === 0) && <LeseText text={notiz?.text} leer={leer} />}
        <MemoListe memos={memos} nurLesen />
      </div>
      <FragenAbschnitt fragen={hier} ich={ich} aufnahmeId={ganz.aufnahme.id} />
    </>
  );
}

function StationInhalt({ ich, ganz, station }: { ich: IchAntwort; ganz: AufnahmeGanz; station: Station }) {
  const { aufnahme, stationen } = ganz;
  const modus = modusVon(ganz);
  // Fragen (seit E3) – erst, wenn die Besichtigung gesendet ist.
  const ichKurz = { id: ich.nutzer.id, name: ich.nutzer.name };
  const gesendet = gespraechMoeglich(aufnahme);
  const fragen = gesendet ? fragenAmGeraet(aufnahme) : [];
  const anDerStation = fragen.filter((f) => f.bezug.tabelle === "station" && f.bezug.id === station.id);
  // Gesendet ohne Nachtrag: nur zum Lesen. Im Nachtrag: Neues dazu, Längen und „Sonstiges“ berichtigen.
  const nurLesen = modus.art === "gesperrt";
  const nachtragId = modus.art === "nachtrag" ? modus.nachtragId : null;
  const post = usePostausgang();
  const st = STATIONEN.find((s) => s.nr === station.nr);

  // Die Notizen zur Station: je Sendung eine – die erste, und je Nachtrag eine.
  const notizenHier = ganz.notizen.filter((n) => n.stationId === station.id);
  const ersteNotiz = notizenHier.find((n) => (n.nachtragId ?? null) === null);
  // Die bearbeitet wird: beim Aufnehmen die erste, im Nachtrag seine.
  const eigeneNotiz = modus.art === "aufnahme" ? ersteNotiz : nachtragId ? notizenHier.find((n) => n.nachtragId === nachtragId) : undefined;
  // Was frühere Nachträge gebracht haben, in ihrer Reihenfolge – nur, was es „gibt“.
  const nachgereicht = ganz.nachtraege
    .filter((n) => n.id !== nachtragId)
    .flatMap((n) => notizenHier.filter((x) => x.nachtragId === n.id).map((notiz) => ({ notiz, nachtrag: n })))
    .filter(({ notiz }) => notiz.text.trim() !== "" || memosZurNotiz(ganz, notiz).length > 0);

  const [notiz, setNotiz] = useState(() => eigeneNotiz?.text ?? "");
  const [sonstiges, setSonstiges] = useState(() => station.sonstiges ?? "");
  const [zuletzt, setZuletzt] = useState<Feld | null>(null);
  const [probleme, setProbleme] = useState<Record<string, LaengeProblem | null>>({});
  const laengeUngueltig = Object.values(probleme).includes("ungueltig");
  const laengeLeer = Object.values(probleme).includes("leer");

  const notizSpeicher = useSpeichern(async (text: string) => {
    await notizSpeichern(station, text);
    setZuletzt("notiz");
  });
  const sonstigesSpeicher = useSpeichern(async (text: string) => {
    await sonstigesSpeichern(station, text);
    setZuletzt("sonstiges");
  });

  const fotos = ganz.fotos.filter((f) => f.stationId === station.id);
  // Die Marke „Notiz“ am Foto: Text oder Sprachmemo.
  const mitNotiz = new Set(
    ganz.notizen.flatMap((n) => (n.fotoId !== null && (n.text.trim() !== "" || memosZurNotiz(ganz, n).length > 0) ? [n.fotoId] : [])),
  );
  // Die Marke „Frage“ am Foto (seit E3): eine offene Frage am Foto oder an seiner Notiz.
  const fotoDerNotiz = new Map(ganz.notizen.flatMap((n) => (n.fotoId !== null ? [[n.id, n.fotoId] as const] : [])));
  const mitFrage = new Set(
    fragen
      .filter((f) => f.stand === "offen")
      .flatMap((f) => (f.bezug.tabelle === "foto" ? [f.bezug.id] : f.bezug.tabelle === "notiz" ? [fotoDerNotiz.get(f.bezug.id) ?? ""] : []))
      .filter(Boolean),
  );
  // Bearbeiten lassen sich beim Aufnehmen alle Fotos, im Nachtrag seine eigenen.
  const bearbeitbar = fotos.filter((f) => modus.art === "aufnahme" || (nachtragId !== null && f.nachtragId === nachtragId));
  const memos = memosZurNotiz(ganz, eigeneNotiz);
  const notizFeld = useRef<HTMLTextAreaElement>(null);
  const sonstigesFeld = useRef<HTMLTextAreaElement>(null);

  const zurUebersicht = `#/aufnahme/${aufnahme.id}`;
  // Unten links: die vorige Station mit ihrem Namen – in Station 1 die Übersicht.
  const vorige = STATIONEN.find((s) => s.nr === station.nr - 1);
  const zurueck = vorige
    ? { adresse: `#/aufnahme/${aufnahme.id}/station/${vorige.nr}`, name: vorige.name, ganz: `Zurück zur Station ${vorige.nr}: ${vorige.name}` }
    : { adresse: zurUebersicht, name: "Übersicht", ganz: "Zurück zur Übersicht der Stationen" };
  // Nach der letzten Station: „Material“ am Schluss (seit E3b), von dort die Dachplanung mit K2.
  const amSchluss = `#/aufnahme/${aufnahme.id}/material`;
  const weiter = station.nr < STATIONEN_ZAHL ? `#/aufnahme/${aufnahme.id}/station/${station.nr + 1}` : amSchluss;
  // Ohne „Station fertig“: weiter zur nächsten Station, nach der letzten zu „Material“.
  const naechste = STATIONEN.find((s) => s.nr === station.nr + 1);
  // Material aus dieser Station (seit E3b); Längen nur, wo die Besichtigung schon welche hat.
  const materialHier = materialOrdnen(materialAn(ganz.materialangaben, station.id), stationen);
  const laengenZeigen = mitLaengen(ganz.laengen);
  const sonstigesMarke =
    nachtragId !== null ? (station.geaendertImNachtrag === nachtragId ? "berichtigt" : null) : nurLesen && station.geaendertImNachtrag ? "berichtigt" : null;

  async function fertig() {
    notizSpeicher.sofort();
    sonstigesSpeicher.sofort();
    try {
      await stationFertig(station);
      toastZeigen(`${st?.name ?? "Station"} fertig`);
      window.location.hash = weiter;
    } catch (fehler) {
      speichernFehlgeschlagen(fehler, "Konnte nicht speichern – bitte noch einmal.");
    }
  }

  // Die Notiz zum Tippen, Diktieren und für Sprachmemos – beim Aufnehmen die zur Station, im Nachtrag der Nachtrag dazu.
  const notizKarte = (
    // Ein Tipp neben das Feld setzt den Cursor hinein – wie beim Feld selbst.
    <div
      className="text-karte"
      onClick={(e) => {
        if (e.target === e.currentTarget) notizFeld.current?.focus();
      }}
    >
      <textarea
        ref={notizFeld}
        id="notiz"
        aria-labelledby="notiz-titel"
        rows={nachtragId ? 3 : 4}
        maxLength={TEXT_GRENZE}
        value={notiz}
        placeholder={nachtragId ? "Was fehlt, was ist dazugekommen? Tippen oder diktieren" : "Was ist hier wichtig? Tippen oder diktieren"}
        onChange={(e) => {
          setNotiz(e.target.value);
          notizSpeicher.spaeter(e.target.value);
        }}
        onBlur={notizSpeicher.sofort}
      />
      <MemoListe memos={memos} />
      <NotizFuss ort={{ aufnahmeId: aufnahme.id, stationId: station.id, fotoId: null }} vorher={notizSpeicher.sofort} gespeichert={zuletzt === "notiz"} />
    </div>
  );

  return (
    <div className="app-seite">
      <AppKopf
        umgebung={ich.umgebung}
        zurueck={{ adresse: zurUebersicht, name: "Übersicht" }}
        titel={
          <>
            Station {station.nr}
            <span className="nur-breit"> von {STATIONEN_ZAHL}</span>
          </>
        }
        // Was noch ans Büro muss – auch hier, nicht nur auf Start und Übersicht (seit 19.10.2026).
        rechts={<SendeStand wartend={post.wartend} />}
        gross={
          <>
            {st?.name}
            {nachtragId ? (
              <Chip art="lime" ikon="plus">
                Nachtrag
              </Chip>
            ) : (
              station.fertig && (
                <Chip art="lime" ikon="haken">
                  fertig
                </Chip>
              )
            )}
          </>
        }
        unter={st?.kurz}
      >
        <div className="station-punkte" aria-hidden="true">
          {stationen.map((s) => (
            <i key={s.id} className={s.id === station.id ? "hier" : s.fertig ? "fertig" : undefined} />
          ))}
        </div>
      </AppKopf>
      <main className="app-inhalt">
        {nachtragId && (
          <Tipp art="gelb" ikon="plus">
            <b>Nachtrag:</b> Neue Fotos, eine Notiz und Material kommen dazu; {laengenZeigen ? "Längen, " : ""}Mengen
            {station.art === "dach" ? ", Höhe, Zugang" : ""} und „Sonstiges“ lassen sich berichtigen. Was gesendet ist, bleibt.
          </Tipp>
        )}
        {/* Fragen an der Station (seit E3) – oben, wo man zuerst hinsieht. */}
        <FragenAbschnitt fragen={anDerStation} ich={ichKurz} aufnahmeId={aufnahme.id} titel="Fragen zu dieser Station" />
        <Tipp art="grau">{st?.erklaer}</Tipp>

        <div className="abschnitt-zeile">
          <div className="abschnitt-titel">Fotos</div>
          {bearbeitbar.length > 0 && (
            <span className="klein-text">
              <Ikon name="stift" groesse="k" /> <Ikon name="notiz" groesse="k" /> antippen: zeichnen oder Notiz
            </span>
          )}
        </div>
        <FotoRaster
          station={station}
          fotos={fotos}
          mitNotiz={mitNotiz}
          mitFrage={mitFrage}
          adresse={(f) => `#/aufnahme/${aufnahme.id}/station/${station.nr}/foto/${f.id}`}
          marke={(f) => sendungsMarke(f, modus)}
          nurLesen={nurLesen}
          loeschbar={(f) => bearbeitbar.includes(f)}
        />

        <div className="abschnitt-titel" id={modus.art === "aufnahme" ? "notiz-titel" : undefined}>
          Notiz zur ganzen Station
        </div>
        {modus.art === "aufnahme" ? (
          notizKarte
        ) : (
          <>
            <NotizLesen ganz={ganz} notiz={ersteNotiz} leer="Keine Notiz zu dieser Station." fragen={fragen} ich={ichKurz} />
            {nachgereicht.map(({ notiz: n, nachtrag }) => (
              <NotizLesen key={n.id} ganz={ganz} notiz={n} leer="" kopf={nachgereichtAm(nachtrag)} fragen={fragen} ich={ichKurz} />
            ))}
          </>
        )}
        {nachtragId && (
          <>
            <div className="abschnitt-titel" id="notiz-titel">
              Nachtrag zur Notiz
            </div>
            {notizKarte}
          </>
        )}

        <MaterialKurz material={materialHier} adresse={`#/aufnahme/${aufnahme.id}/station/${station.nr}/material`} modus={modus} />

        {laengenZeigen && (
          <>
            <div className="abschnitt-titel">Längen</div>
            <div className="laengen-raster">
              {LAENGEN_ARTEN.map((art) => {
                const l = ganz.laengen.find((x) => x.stationId === station.id && x.laengenArt === art.schluessel);
                const meter = l?.meter ?? null;
                return nurLesen ? (
                  <LaengeWert key={art.schluessel} art={art} meter={meter} marke={laengeMarke(l, modus)} />
                ) : (
                  <LaengeFeld
                    key={art.schluessel}
                    station={station}
                    art={art}
                    anfang={meter}
                    marke={laengeMarke(l, modus)}
                    // Gesendet und mit Wert: im Nachtrag nur berichtigen, nicht leeren.
                    leerGehtNicht={nachtragId !== null && l !== undefined && l.nachtragId !== nachtragId && l.meter !== null}
                    gespeichert={setZuletzt}
                    problem={(schluessel, p) => setProbleme((alt) => (alt[schluessel] === p ? alt : { ...alt, [schluessel]: p }))}
                  />
                );
              })}
            </div>
            {laengeUngueltig ? (
              <div className="feld-hinweis">Bitte eine Zahl in Metern, z. B. 12,5 – höchstens 9999.</div>
            ) : laengeLeer ? (
              <div className="feld-hinweis">Im Nachtrag geht leer nicht – trag 0 ein, wenn es keine gibt.</div>
            ) : (
              zuletzt?.startsWith("laenge-") && (
                <div className="klein-text" style={{ marginTop: -6 }}>
                  <Gespeichert an />
                  {nachtragId ? " · Berichtigt kommt im Büro als neu an." : " · Leer heißt: keine Angabe."}
                </div>
              )
            )}
          </>
        )}

        {station.art === "dach" && <DachFelder station={station} modus={modus} />}

        <div className="abschnitt-zeile">
          <div className="abschnitt-titel" id={nurLesen ? undefined : "sonstiges-titel"}>
            Sonstiges
          </div>
          {sonstigesMarke && <span className="sendungs-marke">{sonstigesMarke}</span>}
        </div>
        {nurLesen ? (
          <div className="text-karte nur-lesen">
            <LeseText text={station.sonstiges} leer="Nichts angegeben." />
          </div>
        ) : (
          // Ein Tipp neben das Feld setzt den Cursor hinein – wie bei der Notiz (ein <label> ginge hier nicht: darin steht „Wie diktiere ich?“).
          <div
            className="text-karte"
            onClick={(e) => {
              if (e.target === e.currentTarget) sonstigesFeld.current?.focus();
            }}
          >
            <textarea
              ref={sonstigesFeld}
              id="sonstiges"
              aria-labelledby="sonstiges-titel"
              className="kurz"
              rows={2}
              maxLength={TEXT_GRENZE}
              value={sonstiges}
              placeholder="z. B. Kanal 40×60 im Gang"
              onChange={(e) => {
                setSonstiges(e.target.value);
                sonstigesSpeicher.spaeter(e.target.value);
              }}
              onBlur={sonstigesSpeicher.sofort}
            />
            {/* Kein eigenes Sprachmemo hier – das Büro soll an einer Stelle hören: bei der Notiz oben (Claude, 19.10.2026). */}
            <DiktatHilfe />
            {/* Dass es im Nachtrag „berichtigt“ heißt, sagen der Hinweis oben und die Marke am Titel. */}
            {zuletzt === "sonstiges" && (
              <div className="text-karte-unten">
                <span className="text-karte-wort">
                  <Gespeichert an />
                </span>
              </div>
            )}
          </div>
        )}

        {gesendet && <FrageStellen aufnahmeId={aufnahme.id} bezug={{ tabelle: "station", id: station.id }} ich={ichKurz} wo="zu dieser Station" />}

        <Spaeter titel={station.art === "dach" ? "Weitere Fachfragen zu dieser Station" : "Fachfragen zu dieser Station"} wann="Stufe 2 · nach dem Workshop">
          Was die Firma hier genau wissen muss, legen wir mit dem Team fest – der Platz dafür ist schon da.
        </Spaeter>
      </main>
      <AppFuss>
        <div className="knopf-reihe">
          <a href={zurueck.adresse} className="knopf-rand knopf-vorige" aria-label={zurueck.ganz} title={zurueck.ganz}>
            <Ikon name="links" />
            <span>{zurueck.name}</span>
          </a>
          {modus.art === "aufnahme" ? (
            <button type="button" className="knopf-haupt" onClick={() => void fertig()}>
              <Ikon name="haken" /> Station fertig
            </button>
          ) : naechste ? (
            <a href={`#/aufnahme/${aufnahme.id}/station/${naechste.nr}`} className="knopf-haupt knopf-naechste">
              <span>Weiter: {naechste.name}</span>
              <Ikon name="rechts" />
            </a>
          ) : (
            <a href={amSchluss} className="knopf-haupt knopf-naechste">
              <span>Weiter: Material</span>
              <Ikon name="rechts" />
            </a>
          )}
        </div>
      </AppFuss>
    </div>
  );
}

export function StationSeite({ ich, aufnahmeId, nr }: { ich: IchAntwort; aufnahmeId: string; nr: number }) {
  const ganz = useLive(() => aufnahmeLesen(aufnahmeId), aufnahmeId);
  if (ganz === undefined) return <div className="app-seite" />;
  const station = ganz?.stationen.find((s) => s.nr === nr);
  if (!ganz || !station) {
    return (
      <div className="app-seite">
        <AppKopf umgebung={ich.umgebung} zurueck={ZUM_START} titel="Station" gross="Nicht gefunden" />
        <main className="app-inhalt">
          <Tipp art="grau">Diese Station ist auf diesem Gerät nicht gespeichert.</Tipp>
        </main>
      </div>
    );
  }
  // Je Station und Modus ein eigenes Formular: Beim Wechsel wird gespeichert, was noch offen ist, und neu gelesen –
  // auch, wenn ein Nachtrag beginnt oder endet (dann gehören die Felder zu einer anderen Notiz).
  const modus = modusVon(ganz);
  return <StationInhalt key={`${station.id}-${modus.art === "nachtrag" ? modus.nachtragId : modus.art}`} ich={ich} ganz={ganz} station={station} />;
}
